import React, { useState, useRef } from 'react';
import {
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Button, Text, Spinner, makeStyles, tokens,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
  Badge,
} from '@fluentui/react-components';
import { ArrowImport24Regular } from '@fluentui/react-icons';
import { TransactionInput } from '../types';
import { api } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';

interface ImportCsvDialogProps {
  accountId: number;
  open: boolean;
  onClose: () => void;
  onImported: (rows: any[]) => void;
}

const useStyles = makeStyles({
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
  previewContainer: {
    maxHeight: '400px',
    overflowY: 'auto' as any,
    overflowX: 'auto' as any,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    WebkitOverflowScrolling: 'touch' as any,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  successText: {
    color: tokens.colorPaletteGreenForeground1,
  },
  summary: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
    flexWrap: 'wrap' as any,
  },
  mobilePreviewCard: {
    padding: '10px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  mobilePreviewCardError: {
    padding: '10px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorPaletteRedBackground1,
  },
});

export const ImportCsvDialog: React.FC<ImportCsvDialogProps> = ({ accountId, open, onClose, onImported }) => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<TransactionInput[]>([]);
  const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parseError, setParseError] = useState('');
  const [fileName, setFileName] = useState('');

  const reset = () => {
    setRows([]);
    setErrors({});
    setIsValid(false);
    setLoading(false);
    setImporting(false);
    setParseError('');
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setLoading(true);
    setParseError('');

    try {
      const text = await file.text();
      const result = await api.previewImport(accountId, text);
      setRows(result.rows);
      setErrors(result.errors);
      setIsValid(result.valid);
    } catch (err: any) {
      setParseError(err.message || 'Failed to parse CSV');
      setRows([]);
      setErrors({});
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const result = await api.confirmImport(accountId, rows);
      onImported(result.transactions);
      reset();
      onClose();
    } catch (err: any) {
      setParseError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const validCount = rows.length - Object.keys(errors).length;
  const errorCount = Object.keys(errors).length;

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) handleClose(); }}>
      <DialogSurface style={isMobile ? { maxWidth: '100vw', width: '100vw', margin: 0, borderRadius: '12px 12px 0 0', position: 'fixed', bottom: 0, maxHeight: '90vh' } : { maxWidth: '800px', width: '90vw' }}>
        <DialogTitle>{isMobile ? 'Import CSV' : 'Import Transactions from CSV'}</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div className={styles.fileRow}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Button
                icon={<ArrowImport24Regular />}
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || importing}
                style={isMobile ? { width: '100%' } : {}}
              >
                {fileName || 'Choose CSV file'}
              </Button>
              {loading && <Spinner size="tiny" label="Parsing..." />}
            </div>

            {parseError && (
              <Text className={styles.errorText} block>{parseError}</Text>
            )}

            {rows.length > 0 && (
              <>
                <div className={styles.summary}>
                  <Badge color="informative">{rows.length} rows</Badge>
                  <Badge color="success">{validCount} valid</Badge>
                  {errorCount > 0 && <Badge color="danger">{errorCount} with errors</Badge>}
                </div>
                <div className={styles.previewContainer}>
                  {isMobile ? (
                    /* Mobile: compact card list */
                    rows.map((row, idx) => {
                      const rowErrors = errors[String(idx)];
                      const allErrors = rowErrors ? Object.values(rowErrors).join('; ') : '';
                      return (
                        <div key={idx} className={rowErrors ? styles.mobilePreviewCardError : styles.mobilePreviewCard}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text size={200} weight="semibold">{row.date} · {row.action}</Text>
                            {rowErrors ? (
                              <Text className={styles.errorText}>✗</Text>
                            ) : (
                              <Text className={styles.successText}>✓</Text>
                            )}
                          </div>
                          <Text size={300} weight="bold" block>{row.payee}</Text>
                          <Text size={200}>
                            {row.payment ? `Payment: $${row.payment}` : `Deposit: $${row.deposit}`}
                          </Text>
                          {rowErrors && (
                            <Text className={styles.errorText} size={100} block>{allErrors}</Text>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    /* Desktop: full table */
                    <Table size="small">
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell style={{ width: '30px' }}>#</TableHeaderCell>
                          <TableHeaderCell>Date</TableHeaderCell>
                          <TableHeaderCell>Action</TableHeaderCell>
                          <TableHeaderCell>Payee</TableHeaderCell>
                          <TableHeaderCell>Payment</TableHeaderCell>
                          <TableHeaderCell>Deposit</TableHeaderCell>
                          <TableHeaderCell>Notes</TableHeaderCell>
                          <TableHeaderCell>Status</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((row, idx) => {
                          const rowErrors = errors[String(idx)];
                          const allErrors = rowErrors ? Object.values(rowErrors).join('; ') : '';
                          return (
                            <TableRow key={idx} style={rowErrors ? { backgroundColor: tokens.colorPaletteRedBackground1 } : {}}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>{row.date}</TableCell>
                              <TableCell>{row.action}</TableCell>
                              <TableCell>{row.payee}</TableCell>
                              <TableCell>{row.payment ?? ''}</TableCell>
                              <TableCell>{row.deposit ?? ''}</TableCell>
                              <TableCell>{row.notes ?? ''}</TableCell>
                              <TableCell>
                                {rowErrors ? (
                                  <Text className={styles.errorText}>{allErrors}</Text>
                                ) : (
                                  <Text className={styles.successText}>✓</Text>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={handleClose} disabled={importing}>Cancel</Button>
          <Button
            appearance="primary"
            onClick={handleImport}
            disabled={!isValid || rows.length === 0 || importing}
          >
            {importing ? 'Importing...' : `Import ${validCount} Transaction${validCount !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
